import { useMemo } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchSessionParams } from "@/tools/helper";

export interface UseSessionParamsResult {
  configID?: string;
  language?: string;
  error?: Error;
  isLoading?: boolean;
  hasData: boolean;
}

/**
 * Hook for managing session parameters
 *
 * @description Fetches configuration data from the pgfirst/configuration endpoint.
 * Provides base session configuration that other hooks can override.
 *
 * @param params - Optional URL parameters for the session configuration request
 * @returns Object containing:
 *   - configID: The configuration ID from the API response
 *   - language: The language from the API response
 *   - error: Any error that occurred during the request
 *   - isLoading: Loading state of the request
 *   - hasData: Boolean indicating if there's any session params data
 *
 * @example
 * ```typescript
 * const { configID, language, error, isLoading, hasData } = useSessionParams(params);
 * ```
 */
export const useSessionParams = (
  params?: string | undefined,
): UseSessionParamsResult => {
  const queryClient = useQueryClient();

  const {
    data: sessionParams,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["sessionParams", params],
    queryFn: async () => {
      try {
        const result = await fetchSessionParams(params);
        if (result) {
          queryClient.removeQueries({ queryKey: ["loadedSessionData"] });
          queryClient.removeQueries({ queryKey: ["resetSessionData"] });
        }
        return result;
      } catch (error) {
        console.error("Failed to fetch session params:", error);
        throw error;
      }
    },
    enabled: !!params,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, error) => {
      if (failureCount >= 3) return false;
      if (error instanceof Error && error.message.includes("404")) return false;
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { configID, language, hasData } = useMemo(
    () => ({
      configID: sessionParams?.configID,
      language: sessionParams?.language,
      hasData: !!sessionParams?.configID,
    }),
    [sessionParams],
  );

  return {
    configID,
    language,
    error: error ?? undefined,
    isLoading,
    hasData,
  };
};
